import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { StandaloneAutoContinue } from "@/lib/hooks/use-standalone-auto-continue";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./standalone-return-actions.i18n";

type Props = {
  /** Sanitized return target (see useStandaloneMode). */
  returnUrl: string;
  /** Countdown state from useStandaloneAutoContinue. */
  autoContinue: StandaloneAutoContinue;
};

/**
 * QL-33 / QL-264 / QL-263 — the "return to the partner system" block shown on
 * every hand-off step of the process page (review steps, completed) when
 * the instance was launched standalone with an `internalRedirectUrl`: the
 * countdown notice, the Continue button for an immediate return, and "Stay on
 * this page" while the countdown runs. The cancelled card (QL-737) renders it
 * too, but never runs a countdown there, so it shows the Continue button only.
 */
export function StandaloneReturnActions({ returnUrl, autoContinue }: Props) {
  const t = useT(T);
  const host = new URL(returnUrl).host;
  const countingDown = autoContinue.secondsLeft !== null && !autoContinue.redirecting;

  return (
    <div className="space-y-3">
      {(autoContinue.secondsLeft !== null || autoContinue.redirecting) && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {autoContinue.redirecting
            ? t.returning(host)
            : t.returnIn(host, autoContinue.secondsLeft)}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={autoContinue.continueNow} disabled={autoContinue.redirecting}>
          {t.continue}
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
        {countingDown && (
          <Button variant="ghost" onClick={autoContinue.cancel}>
            {t.stayHere}
          </Button>
        )}
      </div>
    </div>
  );
}
